// src/crypto/identity.js
//
// 🟢 WEB DEVICE IDENTITY — the browser is a first-class Allo device.
//
// The mobile app's E2EE is per-device (services/CryptoService.ts): every device owns
// a TweetNaCl box keypair and publishes its PUBLIC key to
//   users/{uid}.devices[deviceId] = { publicKey, platform, updatedAt }
// A message carries `perDeviceKeys[deviceId] = {text, nonce}` sealed to each recipient
// device + a `senderDeviceId`. To read, a device looks up its own sealed copy and the
// sender's public key for the sending device.
//
// So the web client must:
//   1. generate its OWN box keypair once (persisted in IndexedDB — never leaves the browser)
//   2. register its public key under a stable web deviceId
//   3. use the private key to decrypt its `perDeviceKeys[webDeviceId]` copies
//   4. seal outgoing messages to EVERY device of both parties (phone + web + …)
//
// This is exactly WhatsApp-multidevice: the phone stays the primary; the web is an added
// device. Consequence (same as WhatsApp): messages sent BEFORE this browser paired were
// not sealed to it, so they can't be read on web unless the phone re-shares history.

import nacl from 'tweetnacl';
import util from 'tweetnacl-util';
import { doc, getDoc, setDoc, updateDoc, deleteField } from 'firebase/firestore';
import { seenStampDue } from '../services/sealDevices';   // 🧳 this browser's sign of life
import { db } from '../config/firebase';

const DB_NAME = 'allo_web_identity';
const STORE = 'keys';

// ── minimal IndexedDB (no dep) — private key must persist across reloads/tabs ──
function idb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
async function idbGet(key) {
  const d = await idb();
  return new Promise((resolve, reject) => {
    const tx = d.transaction(STORE, 'readonly').objectStore(STORE).get(key);
    tx.onsuccess = () => resolve(tx.result ?? null);
    tx.onerror = () => reject(tx.error);
  });
}
async function idbSet(key, value) {
  const d = await idb();
  return new Promise((resolve, reject) => {
    const tx = d.transaction(STORE, 'readwrite').objectStore(STORE).put(value, key);
    tx.onsuccess = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

let _identity = null; // { deviceId, publicKey, secretKeyU8 }

function newDeviceId() {
  // Stable, unique, human-recognizable as a web device.
  const rand = util.encodeBase64(nacl.randomBytes(9)).replace(/[^a-zA-Z0-9]/g, '').slice(0, 12);
  return `web_${rand}`;
}

/** Load (or create-and-persist) this browser's device identity. Idempotent. */
export async function getIdentity() {
  if (_identity) return _identity;

  let stored = await idbGet('identity');
  if (!stored) {
    const kp = nacl.box.keyPair();
    stored = {
      deviceId: newDeviceId(),
      publicKey: util.encodeBase64(kp.publicKey),
      secretKey: util.encodeBase64(kp.secretKey),
      createdAt: Date.now(),
    };
    await idbSet('identity', stored);
  }
  _identity = {
    deviceId: stored.deviceId,
    publicKey: stored.publicKey,
    secretKeyU8: util.decodeBase64(stored.secretKey),
  };
  return _identity;
}

// ══════════════════════════════════════════════════════════════════════════
//  PUBLISHING — and why this is verified rather than fired and forgotten
// ══════════════════════════════════════════════════════════════════════════
//
// 🟥 THE BUG THIS EXISTS TO PREVENT: a reader decrypts with the SENDER's public key,
//    looked up as users/{senderId}.devices[senderDeviceId].publicKey. If this browser's
//    entry never landed in Firestore, then NOTHING it sends can be read by ANYONE —
//    not the recipient, and not the sender's own phone. That is the exact signature of
//    "🔒 Encrypted Message on every device including my own": one missing key, every
//    reader failing identically.
//
//    The old version wrote once, at auth, un-awaited by its only caller and wrapped in
//    `.catch(() => {})`. Offline at that moment, a rules hiccup, a slow cold start —
//    the write is lost, nothing ever retries, and the tab goes on sending messages
//    nobody can open. The phone has had `ensureMyKeyPublished` for exactly this reason
//    (CryptoService ~465, read-back-and-repair on every launch); the web had no
//    equivalent at all.
//
//    So: write, READ IT BACK, and re-verify periodically — the check is cheap and
//    cached, and it runs before every seal (see e2ee.sealForUsers).

const VERIFY_TTL = 5 * 60 * 1000;
let _uid = null;
let _verifiedAt = 0;
let _inflight = null;

function deviceEntry(id) {
  return {
    publicKey: id.publicKey,
    platform: 'web',
    updatedAt: Date.now(),
    userAgent: (navigator.userAgent || '').slice(0, 160),
  };
}

/**
 * Make sure this browser's public key is actually readable by everyone else.
 * @returns {Promise<boolean>} true when Firestore is confirmed to hold our key.
 */
export async function ensureKeyPublished({ force = false } = {}) {
  const userId = _uid;
  if (!userId) return false;
  if (!force && Date.now() - _verifiedAt < VERIFY_TTL) return true;
  if (_inflight) return _inflight;

  _inflight = (async () => {
    const id = await getIdentity();
    const ref = doc(db, 'users', userId);
    try {
      const snap = await getDoc(ref);
      const entry = snap.data()?.devices?.[id.deviceId];
      /* 🔐 a REVOKED entry (logged out from another device) is not "published" — this browser was
         let in again, so the mark goes (../services/sealDevices) */
      if (entry?.publicKey === id.publicKey && !entry?.revokedAt) {
        _verifiedAt = Date.now();
        /* 🧳 I AM STILL HERE. Other devices stop sealing for a browser that looks gone
              (../services/sealDevices) — so this one says otherwise, at most once a day,
              on the read it was making anyway. Never on a missing entry (no half entries). */
        if (seenStampDue(entry, Date.now())) {
          updateDoc(ref, { [`devices.${id.deviceId}.lastSeenAt`]: Date.now() }).catch(() => {});
        }
        return true;
      }
      // merge, so a sibling device's entry is never touched
      await setDoc(ref, { devices: { [id.deviceId]: { ...deviceEntry(id), revokedAt: deleteField() } } }, { merge: true });

      // 🟥 READ IT BACK. An un-verified write is indistinguishable from no write at
      //    all, and the failure mode is silent and total.
      const after = await getDoc(ref);
      const ok = after.data()?.devices?.[id.deviceId]?.publicKey === id.publicKey;
      if (ok) { _verifiedAt = Date.now(); return true; }
      console.error('[e2ee] this device\u2019s public key did NOT land in Firestore — messages sent now would be unreadable everywhere');
      return false;
    } catch (e) {
      console.warn('[e2ee] could not verify this device\u2019s public key:', e?.message);
      return false;
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
}

/* 🧳 a tab left open for days still says it is here — the check above, every few hours */
const SEEN_CHECK_EVERY_MS = 6 * 60 * 60 * 1000;
let _seenTimer = null;

/** 🔗 The session is over (a guest link ended, a sign-out): the every-few-hours "still here"
 *  check stops. It ran on in a guest tab after its chat was deleted, re-writing a device entry
 *  under an account that no longer existed. */
export function stopSeenTimer() {
  if (_seenTimer) { try { clearInterval(_seenTimer); } catch {} _seenTimer = null; }
}

/** Publish this device's PUBLIC key under the signed-in user. Run right after auth. */
export async function registerDevice(userId) {
  _uid = userId;
  _verifiedAt = 0;
  const id = await getIdentity();
  await ensureKeyPublished({ force: true });
  if (!_seenTimer && typeof setInterval === 'function') {
    _seenTimer = setInterval(() => { ensureKeyPublished({ force: true }).catch(() => {}); }, SEEN_CHECK_EVERY_MS);
  }
  return id;
}

/**
 * 🧳 THIS BROWSER IS LEAVING — say so before its key is wiped.
 *
 * 🟥 A LOG OUT WIPES THE KEY (services/wipeLocal), so the next log in is a NEW device, and this
 *    entry's key no longer exists anywhere. Every phone went on sealing a copy of every message
 *    for it, for ever. `retiredAt` stops that (../services/sealDevices); the public key stays,
 *    because it is how the messages this browser SENT are still opened.
 *    Best-effort and quick: a log out must never wait on the network.
 */
export async function retireThisDevice() {
  const userId = _uid;
  if (!userId) return false;
  try {
    const id = _identity || await idbGet('identity');
    if (!id?.deviceId) return false;
    await Promise.race([
      updateDoc(doc(db, 'users', userId), { [`devices.${id.deviceId}.retiredAt`]: Date.now() }),
      new Promise((r) => setTimeout(r, 1500)),
    ]);
    return true;
  } catch { return false; }
}

/** Wipe the browser identity (called on logout / unlink). */
export async function clearIdentity() {
  _identity = null;
  _uid = null;
  _verifiedAt = 0;
  try {
    const d = await idb();
    await new Promise((res) => {
      const tx = d.transaction(STORE, 'readwrite').objectStore(STORE).delete('identity');
      tx.onsuccess = res; tx.onerror = res;
    });
  } catch {}
}
